import React, { useEffect, useState } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Groups admin tab — lists every distinct group across users with its owner,
 * member count, and the member list. Read-only for now; useful for tracking
 * how many people are in each shared dining "household".
 */
export default function GroupsAdmin({ authToken }) {
  const [groups, setGroups] = useState([]);
  const [totalGroups, setTotalGroups] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState({}); // {groupName: true/false}

  const fetchGroups = async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await fetch(`${API_URL}/api/admin/groups`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.detail || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      setGroups(data.groups || []);
      setTotalGroups(data.total_groups || 0);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  return (
    <div className="groups-admin-panel" data-testid="groups-admin-panel" style={{ padding: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <h2 style={{ margin: 0, color: '#1e293b' }}>👥 Groups</h2>
        <button
          data-testid="groups-refresh-btn"
          onClick={fetchGroups}
          style={{
            padding: '6px 12px',
            background: '#2563eb',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '0.85rem'
          }}
        >
          🔄 Refresh
        </button>
      </div>

      <p style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: '1rem' }}>
        Every distinct group that users have joined. <strong>ALLDATA</strong> is a reserved
        keyword (not a real group). Groups are formed when a user creates one via{' '}
        <em>Profile → Group</em> and others join.
      </p>

      {loading && <div style={{ padding: '1rem' }}>Loading groups…</div>}

      {error && (
        <div
          style={{
            padding: '0.75rem',
            background: '#fef2f2',
            color: '#dc2626',
            borderRadius: '6px',
            marginBottom: '1rem'
          }}
        >
          {error}
        </div>
      )}

      {!loading && !error && (
        <>
          <div
            style={{
              display: 'flex',
              gap: '12px',
              flexWrap: 'wrap',
              marginBottom: '1rem',
              fontSize: '0.9rem'
            }}
          >
            <div
              style={{
                padding: '8px 14px',
                background: '#f1f5f9',
                borderRadius: '8px',
                color: '#334155'
              }}
            >
              <strong>{totalGroups}</strong> group{totalGroups === 1 ? '' : 's'} total
            </div>
            <div
              style={{
                padding: '8px 14px',
                background: '#f1f5f9',
                borderRadius: '8px',
                color: '#334155'
              }}
            >
              <strong>{groups.reduce((sum, g) => sum + g.member_count, 0)}</strong> total
              memberships
            </div>
          </div>

          {groups.length === 0 ? (
            <div
              style={{
                padding: '2rem',
                textAlign: 'center',
                color: '#64748b',
                background: '#f8fafc',
                borderRadius: '8px'
              }}
            >
              No groups yet. Users can create one from their profile.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {groups.map((g) => {
                const isOpen = !!expanded[g.name];
                return (
                  <div
                    key={g.name}
                    data-testid={`group-row-${g.name}`}
                    style={{
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      background: '#fff'
                    }}
                  >
                    <button
                      type="button"
                      data-testid={`group-toggle-${g.name}`}
                      onClick={() =>
                        setExpanded((prev) => ({ ...prev, [g.name]: !prev[g.name] }))
                      }
                      style={{
                        width: '100%',
                        display: 'grid',
                        gridTemplateColumns: '24px 1fr auto auto auto',
                        gap: '12px',
                        alignItems: 'center',
                        padding: '12px 16px',
                        background: 'transparent',
                        border: 'none',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontSize: '0.95rem'
                      }}
                    >
                      <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                        {isOpen ? '▼' : '▶'}
                      </span>
                      <span style={{ fontWeight: 700, color: '#0f172a' }}>{g.name}</span>
                      <span
                        style={{ fontSize: '0.8rem', color: '#475569' }}
                        title="Group owner"
                      >
                        Owner: <strong>{g.owner || '—'}</strong>
                      </span>
                      <span
                        style={{
                          padding: '3px 10px',
                          background: '#dbeafe',
                          color: '#1e40af',
                          borderRadius: '999px',
                          fontSize: '0.78rem',
                          fontWeight: 600
                        }}
                      >
                        {g.member_count} member{g.member_count === 1 ? '' : 's'}
                      </span>
                      <span
                        style={{
                          padding: '3px 10px',
                          background: '#dcfce7',
                          color: '#166534',
                          borderRadius: '999px',
                          fontSize: '0.78rem',
                          fontWeight: 600
                        }}
                        title="Members with email_opt_in = true"
                      >
                        📧 {g.opt_in_count} opted in
                      </span>
                    </button>
                    {isOpen && (
                      <div
                        style={{
                          borderTop: '1px solid #e2e8f0',
                          padding: '8px 16px 12px 52px',
                          background: '#f8fafc'
                        }}
                      >
                        <table
                          style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            fontSize: '0.85rem'
                          }}
                        >
                          <thead>
                            <tr style={{ color: '#475569' }}>
                              <th style={{ textAlign: 'left', padding: '6px 8px' }}>Name</th>
                              <th style={{ textAlign: 'left', padding: '6px 8px' }}>Email</th>
                              <th style={{ textAlign: 'left', padding: '6px 8px' }}>Role</th>
                              <th style={{ textAlign: 'left', padding: '6px 8px' }}>Email Opt-in</th>
                            </tr>
                          </thead>
                          <tbody>
                            {g.members.map((m) => (
                              <tr key={m.email} style={{ borderTop: '1px solid #e2e8f0' }}>
                                <td style={{ padding: '6px 8px' }}>{m.display_name}</td>
                                <td style={{ padding: '6px 8px', color: '#475569' }}>{m.email}</td>
                                <td style={{ padding: '6px 8px' }}>
                                  {g.owner === m.email ? (
                                    <span
                                      style={{
                                        padding: '2px 8px',
                                        background: '#fef3c7',
                                        color: '#92400e',
                                        borderRadius: '999px',
                                        fontSize: '0.72rem',
                                        fontWeight: 600
                                      }}
                                    >
                                      👑 Owner
                                    </span>
                                  ) : (
                                    <span style={{ color: '#64748b', fontSize: '0.78rem' }}>Member</span>
                                  )}
                                </td>
                                <td style={{ padding: '6px 8px' }}>
                                  {m.email_opt_in ? (
                                    <span style={{ color: '#16a34a' }}>✅ Yes</span>
                                  ) : (
                                    <span style={{ color: '#dc2626' }}>❌ No</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
